// src/app/pages/dashboard/Apps/RetailReadinessReportWrapper.tsx
import React, {useState, FC} from 'react'
import axios from 'axios'
import {DataTable} from 'primereact/datatable'
import {Column} from 'primereact/column'
import {Chart} from 'primereact/chart'
import {InputTextarea} from 'primereact/inputtextarea'
import {InputNumber} from 'primereact/inputnumber'
import {PageTitle} from '../../../../_designereyes/layout/core'
import {useAuth} from '../../../modules/auth'
import {API_URL} from '../../../modules/auth/core/_requests'
import {ChartOptions, ChartData} from 'chart.js'

export interface ReportResult {
  excelFile: string
  reportSummary: {
    totalAsins: number
    successfulAsins: number
    failedAsins: number
    titlesExceedingLength: number
    descriptionsExceedingLength: number
    bulletPointsQty: number
    bulletsLengthOptimized: number
    aplusContentIncluded: number
    imagesQtyOptimized: number
    customerReviewsOptimized: number
    productRatingOptimized: number
  }
  resultData: any[]
  config: {
    minTitleLength: number
    minDescLength: number
    minBulletQty: number
    minBulletLength: number
    minImages: number
    minReviews: number
    minRating: number
  }
}

export const RetailReadinessReportWrapper: FC = () => {
  const {currentUser} = useAuth()

  // Report & form state.
  const [reportResult, setReportResult] = useState<ReportResult | null>(null)
  const [asinsText, setAsinsText] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')

  // Configuration state (with defaults).
  const [minTitleLength, setMinTitleLength] = useState<number>(100)
  const [minDescLength, setMinDescLength] = useState<number>(1600)
  const [minBulletQty, setMinBulletQty] = useState<number>(5)
  const [minBulletLength, setMinBulletLength] = useState<number>(150)
  const [minImages, setMinImages] = useState<number>(6)
  const [minReviews, setMinReviews] = useState<number>(30)
  const [minRating, setMinRating] = useState<number>(4.5)

  // Active tab state for Bootstrap tabs (Overview or Details).
  const [activeTab, setActiveTab] = useState<'overview' | 'details'>('overview')

  // Handle form submission.
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const asins = asinsText
      .split('\n')
      .map((asin) => asin.trim())
      .filter((asin) => asin)
    if (asins.length === 0) {
      alert('Please enter at least one ASIN.')
      return
    }
    try {
      setIsLoading(true)
      setMessage('Processing ASINs...')
      const response = await axios.post(
        `${API_URL}/retail-readiness-report/process`,
        {
          asins,
          minTitleLength,
          minDescLength,
          minBulletQty,
          minBulletLength,
          minImages,
          minReviews,
          minRating,
        },
        {responseType: 'json'}
      )
      const {excelFile, reportSummary, resultData} = response.data
      setMessage('Report generated successfully.')
      setReportResult({
        excelFile,
        reportSummary,
        resultData,
        config: {
          minTitleLength,
          minDescLength,
          minBulletQty,
          minBulletLength,
          minImages,
          minReviews,
          minRating,
        },
      })
    } catch (error) {
      console.error('Error processing ASINs:', error)
      alert('An error occurred while processing ASINs.')
      setMessage('An error occurred.')
    } finally {
      setIsLoading(false)
    }
  }

  // Download Excel file (base64 to Blob conversion).
  const downloadExcel = () => {
    if (!reportResult) return
    const {excelFile} = reportResult
    const byteCharacters = atob(excelFile)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Retail_Readiness_Report.xlsx')
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  // Helper: Render a table cell with conditional formatting.
  // A 2px left border in red is shown if the cell's value is below its threshold.
  const renderConditionalCell = (rowData: any, field: string, threshold: number) => {
    const value = rowData[field]
    return (
      <div
        style={{
          borderLeft: `2px solid ${value < threshold ? '#ff6b6b' : 'transparent'}`,
          paddingLeft: '4px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value}
      </div>
    )
  }

  // --- Prepare dashboard data for the Overview tab ---
  let complianceData: ChartData<'bar'> | null = null
  let complianceOptions: ChartOptions<'bar'> | null = null
  let recommendations: string[] = []
  if (reportResult) {
    const total = reportResult.reportSummary.totalAsins
    // Fixed array for eight fields.
    const categories = [
      'Titles',
      'Descriptions',
      'Bullet Qty',
      'Bullet Length',
      'A+ Content',
      'Images',
      'Reviews',
      'Rating',
    ]
    const percentages = categories.map((cat) => {
      switch (cat) {
        case 'Titles':
          return (reportResult.reportSummary.titlesExceedingLength / total) * 100
        case 'Descriptions':
          return (reportResult.reportSummary.descriptionsExceedingLength / total) * 100
        case 'Bullet Qty':
          return (reportResult.reportSummary.bulletPointsQty / total) * 100
        case 'Bullet Length':
          return (reportResult.reportSummary.bulletsLengthOptimized / total) * 100
        case 'A+ Content':
          return (reportResult.reportSummary.aplusContentIncluded / total) * 100
        case 'Images':
          return (reportResult.reportSummary.imagesQtyOptimized / total) * 100
        case 'Reviews':
          return (reportResult.reportSummary.customerReviewsOptimized / total) * 100
        case 'Rating':
          return (reportResult.reportSummary.productRatingOptimized / total) * 100
        default:
          return 0
      }
    })

    // Use eight tones of blue (varying shades of a single hue).
    const blueTones = [
      '#1976D2',
      '#1E88E5',
      '#2196F3',
      '#42A5F5',
      '#64B5F6',
      '#90CAF9',
      '#BBDEFB',
      '#E3F2FD',
    ]

    complianceData = {
      labels: categories,
      datasets: [
        {
          label: '% Compliance',
          data: percentages,
          backgroundColor: blueTones,
          // These settings add space between bars.
          barThickness: 20,
          minBarLength: 5,
          categoryPercentage: 0.5,
          barPercentage: 0.8,
        },
      ],
    }

    complianceOptions = {
      indexAxis: 'y',
      scales: {
        x: {
          min: 0,
          max: 100,
          ticks: {
            callback: (value: string | number) => `${Number(value)}%`,
            color: '#ffffff', // for dark mode
          },
        },
        y: {
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            color: '#ffffff',
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {display: false},
      },
      maintainAspectRatio: false,
      responsive: true,
    }

    // Build recommendations for any category below 80%.
    const thresholdValue = 80
    categories.forEach((cat, i) => {
      const percent = percentages[i]
      if (percent < thresholdValue) {
        recommendations.push(`Improve ${cat} compliance: only ${percent.toFixed(1)}% compliant.`)
      }
    })
  }

  return (
    <>
      <PageTitle>Retail Readiness Report Generator</PageTitle>
      {currentUser?.decoded?.vendorName === 'Designer Eyes' && (
        <div className='my-4'>
          {/* Configuration Form: Two Columns */}
          <div className='card card-body bg-body text-body m-1'>
            <div className='card-header d-flex align-items-center'>
              <h2 className='card-title m-0 mb-0'>Add ASINs & Configuration</h2>
            </div>
            <div className='card-body '>
              <form onSubmit={handleSubmit}>
                <div className='row d-flex align-items-stretch'>
                  {/* Left Column: ASINs & Generate Button */}
                  <div className='col-md-6 mb-3 pt-2 mt-2'>
                    <div className='h-100 d-flex flex-column'>
                      <label htmlFor='asinsTextarea' className='label mb-4'>
                        ASINs (one per line)
                      </label>
                      <InputTextarea
                        id='asinsTextarea'
                        value={asinsText}
                        onChange={(e) => setAsinsText(e.target.value)}
                        rows={8}
                        autoResize
                        className='form-input mb-3'
                        placeholder='Enter ASINs, one per line'
                      />
                      <button type='submit' className='btn btn-primary mt-auto'>
                        {isLoading ? (
                          <>
                            <span
                              className='spinner-border spinner-border-sm me-2'
                              role='status'
                              aria-hidden='true'
                            ></span>
                            Processing...
                          </>
                        ) : (
                          'Generate Report'
                        )}
                      </button>
                    </div>
                  </div>
                  {/* Right Column: Configuration Fields */}
                  <div className='col-md-6 mb-3 mt-2 pt-2'>
                    <div className='h-100 d-flex flex-column'>
                      <h5 className='mb-3'>Configuration</h5>
                      <div className='row'>
                        <div className='col-md-6 mb-3'>
                          <label htmlFor='minTitleLength' className='label d-block mb-2'>
                            Minimum Title Length
                          </label>
                          <InputNumber
                            id='minTitleLength'
                            value={minTitleLength}
                            onValueChange={(e) => setMinTitleLength(e.value ?? 0)}
                            useGrouping={false}
                            className='form-input'
                          />
                        </div>
                        <div className='col-md-6 mb-3'>
                          <label htmlFor='minDescLength' className='label d-block mb-2'>
                            Minimum Description Length
                          </label>
                          <InputNumber
                            id='minDescLength'
                            value={minDescLength}
                            onValueChange={(e) => setMinDescLength(e.value ?? 0)}
                            useGrouping={false}
                            className='form-input'
                          />
                        </div>
                        <div className='col-md-6 mb-3'>
                          <label htmlFor='minBulletQty' className='label d-block mb-2'>
                            Minimum Bullet Quantity
                          </label>
                          <InputNumber
                            id='minBulletQty'
                            value={minBulletQty}
                            onValueChange={(e) => setMinBulletQty(e.value ?? 0)}
                            useGrouping={false}
                            className='form-input'
                          />
                        </div>
                        <div className='col-md-6 mb-3'>
                          <label htmlFor='minBulletLength' className='label d-block mb-2'>
                            Minimum Bullet Length
                          </label>
                          <InputNumber
                            id='minBulletLength'
                            value={minBulletLength}
                            onValueChange={(e) => setMinBulletLength(e.value ?? 0)}
                            useGrouping={false}
                            className='form-input'
                          />
                        </div>
                        <div className='col-md-6 mb-3'>
                          <label htmlFor='minImages' className='label d-block mb-2'>
                            Minimum Number of Images
                          </label>
                          <InputNumber
                            id='minImages'
                            value={minImages}
                            onValueChange={(e) => setMinImages(e.value ?? 0)}
                            useGrouping={false}
                            className='form-input'
                          />
                        </div>
                        <div className='col-md-6 mb-3'>
                          <label htmlFor='minReviews' className='label d-block mb-2'>
                            Minimum Number of Reviews
                          </label>
                          <InputNumber
                            id='minReviews'
                            value={minReviews}
                            onValueChange={(e) => setMinReviews(e.value ?? 0)}
                            useGrouping={false}
                            className='form-input'
                          />
                        </div>
                        <div className='col-md-6 mb-3'>
                          <label htmlFor='minRating' className='label d-block mb-2'>
                            Minimum Rating
                          </label>
                          <InputNumber
                            id='minRating'
                            value={minRating}
                            onValueChange={(e) => setMinRating(e.value ?? 0)}
                            mode='decimal'
                            step={0.1}
                            useGrouping={false}
                            className='form-input'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Report Section */}
          {reportResult && (
            <div className='card'>
              <div className='card-header'>
                <ul className='nav nav-tabs card-header-tabs'>
                  <li className='nav-item'>
                    <button
                      className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                      onClick={() => setActiveTab('overview')}
                    >
                      Overview
                    </button>
                  </li>
                  <li className='nav-item'>
                    <button
                      className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                      onClick={() => setActiveTab('details')}
                    >
                      Details
                    </button>
                  </li>
                </ul>
              </div>
              <div className='card-body'>
                {activeTab === 'overview' && reportResult && (
                  <>
                    {/* Compliance Overview: Horizontal Bar Chart */}
                    <div className='row'>
                      <div className='col-md-12'>
                        <h5>Compliance Overview</h5>
                        <div style={{height: 'auto'}}>
                          <Chart type='bar' data={complianceData!} options={complianceOptions!} />
                        </div>
                      </div>
                    </div>
                    {/* Recommendations Section with extra top margin */}
                    <div className='row mt-4'>
                      <div className='col-md-12'>
                        <div className='card'>
                          <div className='card-header mt-5'>
                            <h5>Recommendations</h5>
                          </div>
                          <div className='card-body'>
                            {recommendations.length > 0 ? (
                              <ul className='mb-0'>
                                {recommendations.map((rec) => (
                                  <li key={rec}>{rec}</li>
                                ))}
                              </ul>
                            ) : (
                              <p>All compliance levels are satisfactory.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {activeTab === 'details' && reportResult && (
                  <>
                    <div className='d-flex justify-content-end mb-3'>
                      <button className='btn btn-secondary' onClick={downloadExcel}>
                        Download Excel
                      </button>
                    </div>
                    <DataTable
                      value={reportResult.resultData}
                      paginator
                      rows={10}
                      responsiveLayout='scroll'
                      className='table-sm'
                    >
                      <Column field='ASIN' header='ASIN' sortable />
                      <Column
                        field='URL'
                        header='URL'
                        sortable
                        body={(rowData: any) => (
                          <a href={rowData.URL} target='_blank' rel='noopener noreferrer'>
                            {rowData.URL}
                          </a>
                        )}
                      />
                      <Column field='Existing Title' header='Title' sortable />
                      <Column field='Brand' header='Brand' sortable />
                      <Column
                        field='Title Length (Characters)'
                        header='Title Length'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Title Length (Characters)',
                            reportResult.config.minTitleLength
                          )
                        }
                      />
                      <Column
                        field='Bullet Points'
                        header='Bullet Points'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Bullet Points',
                            reportResult.config.minBulletQty
                          )
                        }
                      />
                      <Column
                        field='Bullet 1 Length (Characters)'
                        header='Bullet 1 Length'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Bullet 1 Length (Characters)',
                            reportResult.config.minBulletLength
                          )
                        }
                      />
                      <Column
                        field='Bullet 2 Length (Characters)'
                        header='Bullet 2 Length'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Bullet 2 Length (Characters)',
                            reportResult.config.minBulletLength
                          )
                        }
                      />
                      <Column
                        field='Bullet 3 Length (Characters)'
                        header='Bullet 3 Length'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Bullet 3 Length (Characters)',
                            reportResult.config.minBulletLength
                          )
                        }
                      />
                      <Column
                        field='Bullet 4 Length (Characters)'
                        header='Bullet 4 Length'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Bullet 4 Length (Characters)',
                            reportResult.config.minBulletLength
                          )
                        }
                      />
                      <Column
                        field='Bullet 5 Length (Characters)'
                        header='Bullet 5 Length'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Bullet 5 Length (Characters)',
                            reportResult.config.minBulletLength
                          )
                        }
                      />
                      <Column
                        field='Description Length (Characters)'
                        header='Description Length'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'Description Length (Characters)',
                            reportResult.config.minDescLength
                          )
                        }
                      />
                      <Column
                        field='No. Images'
                        header='Images'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(
                            rowData,
                            'No. Images',
                            reportResult.config.minImages
                          )
                        }
                      />
                      <Column field='A+ Content' header='A+ Content' sortable />
                      <Column
                        field='Rating'
                        header='Rating'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(rowData, 'Rating', reportResult.config.minRating)
                        }
                      />
                      <Column
                        field='Reviews'
                        header='Reviews'
                        sortable
                        body={(rowData: any) =>
                          renderConditionalCell(rowData, 'Reviews', reportResult.config.minReviews)
                        }
                      />
                    </DataTable>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default RetailReadinessReportWrapper